import React, { useEffect, useState, useCallback } from 'react';
import { adminGetForecast } from '../../lib/api';
import { RefreshCw, TrendingUp, Boxes, Truck, AlertTriangle } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ForecastPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminGetForecast()); } catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const rows = data?.rows || [];
  const unmatched = (data?.incoming || []).filter((i) => !i.matched);

  return (
    <div className="space-y-8" data-testid="forecast-panel">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-alp-cream">Sales Forecast</h2>
          <p className="text-alp-gray text-sm mt-1">What your stock on hand and incoming shipments are worth if it all sells &mdash; based on live inventory and product-panel costs, not purchase entries.</p>
        </div>
        <button onClick={load} data-testid="forecast-refresh" className="inline-flex items-center gap-2 border border-alp-line text-alp-cream px-4 py-2 text-xs tracking-[0.15em] uppercase rounded hover:border-alp-gold hover:text-alp-gold transition-colors"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh</button>
      </div>

      {loading && !data ? (
        <p className="text-alp-gray py-12 text-center">Loading forecast...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="forecast-onhand-card">
              <div className="flex items-center gap-2 text-alp-gray text-[11px] tracking-[0.2em] uppercase mb-3"><Boxes size={14} className="text-alp-gold" /> On Hand ({t.onHandUnits || 0} units)</div>
              <div className="font-serif text-3xl text-alp-cream">{money(t.onHandRevenue)}</div>
              <div className="text-alp-gray text-xs mt-1">potential revenue</div>
              <div className="flex justify-between text-sm mt-3 pt-3 border-t border-alp-line/60">
                <span className="text-alp-gray">Cost tied up: <span className="text-alp-cream">{money(t.stockCost)}</span></span>
                <span className="text-emerald-400">{money(t.onHandProfit)} profit</span>
              </div>
            </div>
            <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="forecast-incoming-card">
              <div className="flex items-center gap-2 text-alp-gray text-[11px] tracking-[0.2em] uppercase mb-3"><Truck size={14} className="text-alp-gold" /> Incoming ({t.incomingUnits || 0} units)</div>
              <div className="font-serif text-3xl text-alp-cream">{money(t.incomingRevenue)}</div>
              <div className="text-alp-gray text-xs mt-1">potential revenue on the way</div>
              <div className="flex justify-end text-sm mt-3 pt-3 border-t border-alp-line/60">
                <span className="text-emerald-400">{money(t.incomingProfit)} profit</span>
              </div>
            </div>
            <div className="border border-alp-gold/40 rounded-lg bg-alp-gold/5 p-5" data-testid="forecast-combined-card">
              <div className="flex items-center gap-2 text-alp-gold text-[11px] tracking-[0.2em] uppercase mb-3"><TrendingUp size={14} /> Combined Potential</div>
              <div className="font-serif text-3xl text-alp-gold">{money(t.combinedRevenue)}</div>
              <div className="text-alp-gray text-xs mt-1">if everything on hand + incoming sells</div>
              <div className="flex justify-end text-sm mt-3 pt-3 border-t border-alp-gold/20">
                <span className="text-emerald-400">{money(t.combinedProfit)} profit</span>
              </div>
            </div>
          </div>

          {unmatched.length > 0 && (
            <div className="border border-amber-500/40 rounded-lg bg-amber-500/5 p-4 text-sm text-amber-200/90 flex items-start gap-3" data-testid="forecast-unmatched-note">
              <AlertTriangle size={17} className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                Not counted yet: {unmatched.map((i) => `${i.name}${i.eta ? ` (ETA ${i.eta})` : ''}`).join(', ')}.
                <span className="block text-amber-200/60 text-xs mt-1">Open Marketing &rarr; Upcoming Products and set a linked product + expected quantity to include them here.</span>
              </div>
            </div>
          )}

          <div className="border border-alp-line rounded-lg bg-alp-card/40 overflow-x-auto" data-testid="forecast-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-alp-gray border-b border-alp-line">
                  <th className="px-4 py-3">Product</th>
                  <th className="px-3 py-3 text-right">On Hand</th>
                  <th className="px-3 py-3 text-right">Incoming</th>
                  <th className="px-3 py-3 text-right">Unit Cost</th>
                  <th className="px-3 py-3 text-right">Price</th>
                  <th className="px-3 py-3 text-right">Potential Revenue</th>
                  <th className="px-4 py-3 text-right">Potential Profit</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-alp-gray">No stock on hand or incoming.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-b border-alp-line/50 hover:bg-alp-dark/40 transition-colors" data-testid={`forecast-row-${r.id}`}>
                    <td className="px-4 py-3 text-alp-cream">{r.name}{r.size ? <span className="text-alp-gray text-xs ml-2">{r.size}</span> : null}</td>
                    <td className="px-3 py-3 text-right text-alp-cream">{r.inventory}</td>
                    <td className="px-3 py-3 text-right">{r.incoming > 0 ? <span className="text-alp-gold">+{r.incoming}</span> : <span className="text-alp-gray">&mdash;</span>}</td>
                    <td className="px-3 py-3 text-right text-alp-gray">{money(r.unitCost)}</td>
                    <td className="px-3 py-3 text-right text-alp-cream">{money(r.price)}</td>
                    <td className="px-3 py-3 text-right text-alp-cream">{money(r.totalRevenue)}</td>
                    <td className={`px-4 py-3 text-right ${r.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{money(r.totalProfit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-alp-gray text-xs">Unit costs come from each product&apos;s panel (supplier cost + extra cost). Update them on the Products tab and this forecast &mdash; and your P&amp;L &mdash; recalculate instantly.</p>
        </>
      )}
    </div>
  );
}
